import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatCard from '../../../shared/layout/cards/StatCard';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import { fetchAdminScannedBills, fetchAdminScannedBillStats, bulkAdminScannedBills } from '../../../services/Http/AdminApi';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../context/CurrencyProvider';

const AdminScannedBills = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [perPage, setPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkStatus, setBulkStatus] = useState('admin_review');

    const loadStats = async () => {
        try {
            const data = await fetchAdminScannedBillStats();
            setStats(data);
        } catch (err) {
            // non-blocking
        }
    };

    const loadScans = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchAdminScannedBills({
                per_page: perPage,
                page: currentPage,
                status: statusFilter || undefined,
            });
            const rowsData = data?.data || data || [];
            const meta = data?.meta;
            setRows(rowsData);
            setTotalRows(meta?.total ?? rowsData.length);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [perPage, currentPage, statusFilter]);

    useEffect(() => {
        loadStats();
        loadScans();
    }, [loadScans]);

    const columns = useMemo(() => ([
        {
            name: t('ScannedBills.columns.createdAt'),
            selector: row => row.created_at,
            sortable: true,
            width: '180px',
        },
        {
            name: t('ScannedBills.columns.user'),
            selector: row => row.user?.email || '-',
            sortable: false,
            grow: 1.2,
        },
        {
            name: t('ScannedBills.columns.organization'),
            selector: row => row.organization?.name || '-',
            sortable: false,
            grow: 1.2,
        },
        {
            name: t('ScannedBills.columns.status'),
            cell: row => <StatusPill $status={row.status}>{t(`ScannedBills.statuses.${row.status}`, { defaultValue: row.status })}</StatusPill>,
            sortable: true,
            width: '160px',
        },
        {
            name: t('ScannedBills.columns.total'),
            selector: row => row.total_amount ? formatResolvedAmount({
                eurAmount: row.total_amount,
                localAmount: row.total_amount_local,
                localCurrencyId: row.currency_id,
            }) : '-',
            sortable: false,
            width: '140px',
        },
        {
            name: t('ScannedBills.columns.actions'),
            cell: row => (
                <ActionButton type="button" onClick={() => navigate(`/scanned-bills/${row.id}`)}>
                    {t('ScannedBills.view')}
                </ActionButton>
            ),
            width: '140px',
        },
    ]), [navigate, t, formatResolvedAmount]);

    const tableCustomStyles = useMemo(() => ({
        table: {
            style: {
                backgroundColor: 'transparent',
            },
        },
        headRow: {
            style: {
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                border: '1px solid var(--color-primary-bg-soft)',
                borderBottom: '1px solid var(--color-primary-bg-soft)',
                backgroundColor: 'rgba(124, 58, 237, 0.03)',
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                color: '#4b5563',
                fontSize: '12px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
            },
        },
        rows: {
            style: {
                borderLeft: '1px solid #e5e7eb',
                borderRight: '1px solid #e5e7eb',
                borderBottom: '1px solid #e5e7eb',
                minHeight: '60px',
                backgroundColor: '#fff',
                '&:hover': {
                    backgroundColor: 'rgba(124, 58, 237, 0.02)',
                },
            },
        },
        cells: {
            style: {
                fontSize: '13px',
                color: '#111827',
            },
        },
        pagination: {
            style: {
                border: '1px solid #e5e7eb',
                borderTop: 'none',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                backgroundColor: '#fff',
            },
        },
    }), []);

    const handleBulkAction = async () => {
        if (selectedRows.length === 0) return;
        await bulkAdminScannedBills({
            ids: selectedRows.map((row) => row.id),
            status: bulkStatus,
        });
        setSelectedRows([]);
        loadScans();
        loadStats();
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('ScannedBills.adminTitle')}</PageTitle>
            </PageHeader>
            <PageContent>
                {stats && (
                    <StatsGrid>
                        <StatCard title={t('ScannedBills.stats.total')} value={stats.total ?? 0} />
                        <StatCard title={t('ScannedBills.stats.pending')} value={stats.pending ?? 0} />
                        <StatCard title={t('ScannedBills.stats.needsReview')} value={stats.needs_review ?? 0} />
                        <StatCard title={t('ScannedBills.stats.processed')} value={stats.processed ?? 0} />
                        <StatCard title={t('ScannedBills.stats.rejected')} value={stats.rejected ?? 0} />
                    </StatsGrid>
                )}

                <FilterBar>
                    <FilterField>
                        <FilterLabel htmlFor="scan-status-filter">{t('ScannedBills.filters.status')}</FilterLabel>
                        <StyledSelect
                            id="scan-status-filter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">{t('ScannedBills.filters.all')}</option>
                            <option value="user_confirmed">user_confirmed</option>
                            <option value="admin_review">admin_review</option>
                            <option value="org_review">org_review</option>
                            <option value="processed">processed</option>
                            <option value="rejected">rejected</option>
                        </StyledSelect>
                    </FilterField>
                    <FilterField>
                        <FilterLabel>{t('ScannedBills.bulkAction')}</FilterLabel>
                        <StyledSelect
                            value={bulkStatus}
                            onChange={(e) => setBulkStatus(e.target.value)}
                        >
                            <option value="admin_review">admin_review</option>
                            <option value="org_review">org_review</option>
                            <option value="processed">processed</option>
                            <option value="rejected">rejected</option>
                        </StyledSelect>
                    </FilterField>
                    <BulkActionButton
                        type="button"
                        onClick={handleBulkAction}
                        disabled={selectedRows.length === 0}
                    >
                        {t('ScannedBills.bulkAction')} ({selectedRows.length})
                    </BulkActionButton>
                </FilterBar>

                {loading ? (
                    <LoadingMessage>{t('ScannedBills.loading')}</LoadingMessage>
                ) : error ? (
                    <ErrorMessage>{t('ScannedBills.error')}</ErrorMessage>
                ) : (
                    <DataTable
                        columns={columns}
                        data={rows}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={setPerPage}
                        onChangePage={setCurrentPage}
                        customStyles={tableCustomStyles}
                        dense
                    />
                )}
            </PageContent>
        </PageContainer>
    );
};

export default AdminScannedBills;

const FilterBar = styled.div`
    margin-bottom: 1rem;
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    align-items: flex-end;
    padding: 0.75rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.surface};
    border-radius: 12px;
`;

const FilterField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    min-width: 180px;
`;

const FilterLabel = styled.label`
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textMuted};
`;

const StyledSelect = styled(SimpleSelect)`
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.surfaceAlt || theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-radius: 10px;
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
    min-height: 40px;
    outline: none;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
    }
`;

const BulkActionButton = styled.button`
    border: none;
    background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
    color: #fff;
    border-radius: 10px;
    padding: 0.625rem 0.875rem;
    font-weight: 700;
    cursor: pointer;
    min-height: 40px;
    transition: opacity 0.2s ease;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }

    &:not(:disabled):hover {
        opacity: 0.92;
    }
`;

const ActionButton = styled.button`
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.surfaceAlt || '#fff'};
    color: var(--color-primary);
    border-radius: 8px;
    padding: 0.5rem 0.625rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
        border-color: var(--color-primary-bg-soft);
        background: var(--color-primary-bg);
    }
`;

const StatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    padding: 0.375rem 0.625rem;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border: 1px solid;

    ${({ $status }) => {
        switch ($status) {
        case 'processed':
            return `
                color: #166534;
                background: #ecfdf5;
                border-color: #a7f3d0;
            `;
        case 'rejected':
        case 'retailer_rejected':
            return `
                color: #991b1b;
                background: #fef2f2;
                border-color: #fecaca;
            `;
        case 'admin_review':
        case 'org_review':
        case 'pending_retailer':
            return `
                color: #92400e;
                background: #fffbeb;
                border-color: #fde68a;
            `;
        default:
            return `
                color: #1e40af;
                background: #eff6ff;
                border-color: #bfdbfe;
            `;
        }
    }}
`;
